import type { Occurrence } from '@klopotun/contracts';
import { formatMoney } from '@klopotun/domain';

/**
 * What a payment has cost so far, in the currency the totals are kept in. The equivalent is read
 * off every confirmed period as it was frozen on the day (design note C6), never recomputed - so
 * a period that could not be converted is counted apart rather than guessed at.
 */
export type PaidTotal = {
  amountMinor: number;
  /** Empty until a converted period has said which currency the total is in. */
  currency: string | null;
  unconvertedCount: number;
};

const APPROXIMATELY = '≈';

const NON_BREAKING_SPACE = ' ';

const emptyTotal = (): PaidTotal => ({ amountMinor: 0, currency: null, unconvertedCount: 0 });

const addPeriod = (total: PaidTotal, occurrence: Occurrence): PaidTotal => {
  const { baseAmountMinor, fxBaseCurrency } = occurrence;
  const isConverted = baseAmountMinor !== null && fxBaseCurrency !== null;
  const isSameCurrency =
    isConverted && (total.currency === null || total.currency === fxBaseCurrency);

  if (!isSameCurrency || baseAmountMinor === null) {
    return { ...total, unconvertedCount: total.unconvertedCount + 1 };
  }

  return {
    amountMinor: total.amountMinor + baseAmountMinor,
    currency: fxBaseCurrency,
    unconvertedCount: total.unconvertedCount,
  };
};

export const paidTotalsByPayment = (occurrences: Occurrence[]): Map<string, PaidTotal> =>
  occurrences
    .filter((occurrence) => occurrence.status === 'paid')
    .reduce((totals, occurrence) => {
      const known = totals.get(occurrence.paymentId) ?? emptyTotal();

      return totals.set(occurrence.paymentId, addPeriod(known, occurrence));
    }, new Map<string, PaidTotal>());

/** "разом 3 588,00 UAH", approximate when a period had to be left out; nothing when nothing was paid. */
export const formatPaidTotal = (total: PaidTotal | undefined): string | null => {
  if (total === undefined || total.currency === null) {
    return null;
  }

  const money = formatMoney({ amountMinor: total.amountMinor, currency: total.currency });
  const marked =
    total.unconvertedCount > 0 ? `${APPROXIMATELY}${NON_BREAKING_SPACE}${money}` : money;

  return `разом ${marked}`;
};
